/**
 * `/teacher/prep/:lectureId` — the lecture-prep workbench (layout B): derivation |
 * model | problems + interpretations, with the history strip underneath. A
 * problem session swaps the derivation for recaps. Below `lg` everything stacks
 * as model, derivation, interpretations, problems, history.
 */
import { Component, Suspense, useCallback, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, RotateCcw } from "lucide-react";
import type { Preset, PrepLecture } from "@content/prepSchema";
import { Markdown } from "@client/components/Markdown";
import { Button, EmptyState, PageHeader, Spinner } from "@client/components/ui";
import { ApiError } from "@client/api/client";
import { useLang, useT } from "@client/i18n";
import { formatDayShort } from "@client/lib/format";
import { TeacherNav } from "../TeacherNav";
import { usePrepLecture } from "./api";
import { MODEL_COMPONENTS } from "./models/registry";
import { BookBox } from "./panels/BookBox";
import { DerivationPanel } from "./panels/DerivationPanel";
import { HistoryStrip } from "./panels/HistoryStrip";
import { InterpretationsPanel } from "./panels/InterpretationsPanel";
import { Panel } from "./panels/Panel";
import { ProblemCards } from "./panels/ProblemCards";
import { RecapPanel } from "./panels/RecapPanel";
import { usePrepMemory } from "./usePrepMemory";

interface ActivePreset {
  lectureId: string;
  presetId: string;
  preset: Preset;
  /** Bumped on every load so re-clicking the same preset resets the model. */
  nonce: number;
}

class ModelErrorBoundary extends Component<
  { children: ReactNode; message: string },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override render() {
    return this.state.failed ? (
      <p role="alert" className="text-sm text-ink-600 dark:text-ink-400">
        {this.props.message}
      </p>
    ) : (
      this.props.children
    );
  }
}

function ModelPanel({
  lecture,
  active,
  className,
}: {
  lecture: PrepLecture;
  active: ActivePreset | null;
  className: string;
}) {
  const t = useT();
  const [dismissed, setDismissed] = useState<string | null>(null);
  const stamp = active ? `${active.presetId}:${active.nonce}` : null;
  const Model = active ? MODEL_COMPONENTS[active.preset.model] : null;
  return (
    <Panel title={t("teacher.prep.panel.model")} className={className}>
      <BookBox>{lecture.modelBook}</BookBox>
      {active && Model && stamp ? (
        <ModelErrorBoundary message={t("teacher.prep.model.error")}>
          <Suspense fallback={<Spinner size="md" label={t("common.loading")} />}>
            <Model
              key={stamp}
              state={active.preset.state}
              {...(active.preset.caption && dismissed !== stamp
                ? { caption: active.preset.caption }
                : {})}
              onCaptionDismiss={() => setDismissed(stamp)}
            />
          </Suspense>
        </ModelErrorBoundary>
      ) : (
        <p className="text-sm text-ink-600 dark:text-ink-400">{t("teacher.prep.model.empty")}</p>
      )}
      <div className="text-sm text-ink-600 dark:text-ink-400">
        <p className="font-medium">{t("teacher.prep.limits")}</p>
        <Markdown variant="compact">{lecture.limits}</Markdown>
      </div>
    </Panel>
  );
}

export function TeacherPrepWorkbenchPage() {
  const { lectureId = "" } = useParams<{ lectureId: string }>();
  const t = useT();
  const { lang } = useLang();
  const query = usePrepLecture(lectureId);
  const memory = usePrepMemory(lectureId);
  const [activeState, setActive] = useState<ActivePreset | null>(null);
  const modelRef = useRef<HTMLDivElement>(null);
  const notPrepared =
    query.error instanceof ApiError &&
    (query.error.code === "NOT_FOUND" || query.error.code === "VALIDATION");

  const lecture = query.data?.lecture;
  const active = activeState?.lectureId === lectureId ? activeState : null;

  const showPreset = useCallback(
    (presetId: string) => {
      const preset = lecture?.presets[presetId];
      if (!preset) return;
      setActive((previous) => ({
        lectureId,
        presetId,
        preset,
        nonce: (previous?.nonce ?? 0) + 1,
      }));
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      modelRef.current?.scrollIntoView?.({
        behavior: reduce ? "auto" : "smooth",
        block: "nearest",
      });
    },
    [lecture, lectureId],
  );

  const back = (
    <Link to="/teacher/prep" className="inline-flex items-center gap-1 hover:underline">
      <ArrowLeft className="h-3 w-3" aria-hidden="true" />
      {t("teacher.prep.back")}
    </Link>
  );

  const title = notPrepared
    ? t("teacher.prep.notAvailable")
    : lecture
      ? `${lecture.id} ${lecture.title}`
      : lectureId;

  const resetButton = lecture ? (
    <Button
      variant="secondary"
      size="sm"
      iconLeft={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
      disabled={!active}
      onClick={() => active && showPreset(active.presetId)}
    >
      {t("teacher.prep.reset")}
    </Button>
  ) : undefined;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow={back}
        title={title}
        {...(query.data?.date ? { description: formatDayShort(query.data.date, lang) } : {})}
        {...(resetButton ? { actions: resetButton } : {})}
      />
      <TeacherNav />

      {query.isLoading && <Spinner size="md" label={t("common.loading")} />}
      {query.isError && !notPrepared && <EmptyState title={t("common.error")} />}

      {lecture && (
        <div
          key={lecture.id}
          className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.8fr)_minmax(0,1fr)]"
        >
          <div ref={modelRef} className="order-1 min-w-0 scroll-mt-4 lg:col-start-2 lg:row-start-1">
            <ModelPanel lecture={lecture} active={active} className="h-full" />
          </div>

          {lecture.kind === "lecture" ? (
            <DerivationPanel
              derivation={lecture.derivation}
              revealed={memory.revealed}
              onToggle={memory.toggleRevealed}
              onShow={showPreset}
              className="order-2 lg:col-start-1 lg:row-span-2 lg:row-start-1"
            />
          ) : (
            <RecapPanel
              recaps={lecture.recaps}
              className="order-2 lg:col-start-1 lg:row-span-2 lg:row-start-1"
            />
          )}

          {lecture.interpretations && (
            <InterpretationsPanel
              data={lecture.interpretations}
              openPanels={memory.openPanels}
              onTogglePanel={memory.togglePanel}
              onShow={showPreset}
              className={
                lecture.kind === "lecture"
                  ? "order-3 lg:col-start-3 lg:row-start-2"
                  : "order-3 lg:col-start-3 lg:row-span-2 lg:row-start-1"
              }
            />
          )}

          <ProblemCards
            problems={lecture.problems}
            openPanels={memory.openPanels}
            onTogglePanel={memory.togglePanel}
            onShow={showPreset}
            className={
              lecture.kind === "lecture"
                ? "order-4 lg:col-start-3 lg:row-start-1"
                : "order-4 lg:col-start-2 lg:row-start-2"
            }
          />

          {lecture.history && (
            <HistoryStrip
              data={lecture.history}
              activePreset={active?.presetId ?? null}
              onShow={showPreset}
              className="order-5 lg:col-span-3 lg:row-start-3"
            />
          )}
        </div>
      )}
    </div>
  );
}
